import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LogOut, Menu, X, BarChart2, Trophy, CalendarDays, Layers, User, Building, ChevronDown, Crown } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useOrganization } from '../hooks/useOrganization';
import Logo from './Logo';
import LiquidGlass from './LiquidGlass';

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { userProfile } = useOrganization();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isSuperAdmin = userProfile?.role === 'SuperAdmin';

  useEffect(() => {
    // Handle clicks outside of the dropdown
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    if (signingOut) return;
    
    setSigningOut(true);
    try {
      localStorage.clear();
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.warn('Sign out completed with warnings:', error);
      }
      window.location.href = '/login';
    } catch (err) {
      console.warn('Error during sign out:', err);
      window.location.href = '/login';
    } finally {
      setSigningOut(false);
    }
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsProfileDropdownOpen(false);
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const getInitials = (firstName?: string | null, lastName?: string | null, email?: string) => {
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (firstName) {
      return firstName[0].toUpperCase();
    }
    if (email) {
      return email[0].toUpperCase();
    }
    return 'U';
  };

  const getDisplayName = () => {
    // Prioritize first name from profile
    if (userProfile?.first_name?.trim()) {
      return userProfile.first_name.trim();
    }
    // Fallback to email username
    if (user?.email) {
      return user.email.split('@')[0];
    }
    return 'Account';
  };

  const getFullDisplayName = () => {
    if (userProfile?.first_name && userProfile?.last_name) {
      return `${userProfile.first_name} ${userProfile.last_name}`;
    }
    if (userProfile?.first_name) {
      return userProfile.first_name;
    }
    return user?.email || 'Account';
  };

  return (
    <nav className="relative z-50">
      <LiquidGlass variant="nav" className="mx-4 mt-4 mb-2" bgOpacity="high">
        <div className="container mx-auto px-4">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center" onClick={closeMenu}>
                <Logo className="h-6 text-gray-800" />
              </Link>
            </div>

            {user && (
              <div className="flex items-center sm:hidden">
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-gray-800 hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-gray-300 transition-all duration-200"
                  aria-expanded="false"
                >
                  <span className="sr-only">Open main menu</span>
                  {isMenuOpen ? (
                    <X className="block h-6 w-6" />
                  ) : (
                    <Menu className="block h-6 w-6" />
                  )}
                </button>
              </div>
            )}

            <div className="hidden sm:flex sm:items-center sm:gap-2">
              {user ? (
                <>
                  {isSuperAdmin && (
                    <Link
                      to="/super-admin"
                      className={`relative text-gray-600 hover:text-gray-800 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isActive('/super-admin') ? 'text-gray-800' : 'hover:bg-white/30'
                      }`}
                    >
                      {isActive('/super-admin') && (
                        <div className="absolute inset-0 bg-gray-100/80 backdrop-blur-sm rounded-lg border border-gray-200/60"></div>
                      )}
                      <div className="relative flex items-center gap-2">
                        <Crown className="w-4 h-4" />
                        Super Admin
                      </div>
                    </Link>
                  )}
                  <Link
                    to="/results"
                    className={`relative text-gray-600 hover:text-gray-800 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive('/results') ? 'text-gray-800' : 'hover:bg-white/30'
                    }`}
                  >
                    {isActive('/results') && (
                      <div className="absolute inset-0 bg-gray-100/80 backdrop-blur-sm rounded-lg border border-gray-200/60"></div>
                    )}
                    <div className="relative flex items-center gap-2">
                      <Trophy className="w-4 h-4" />
                      Results
                    </div>
                  </Link>
                  <Link
                    to="/events"
                    className={`relative text-gray-600 hover:text-gray-800 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive('/events') ? 'text-gray-800' : 'hover:bg-white/30'
                    }`}
                  >
                    {isActive('/events') && (
                      <div className="absolute inset-0 bg-gray-100/80 backdrop-blur-sm rounded-lg border border-gray-200/60"></div>
                    )}
                    <div className="relative flex items-center gap-2">
                      <CalendarDays className="w-4 h-4" />
                      Events
                    </div>
                  </Link>
                  <Link
                    to="/multi-race"
                    className={`relative text-gray-600 hover:text-gray-800 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive('/multi-race') ? 'text-gray-800' : 'hover:bg-white/30'
                    }`}
                  >
                    {isActive('/multi-race') && (
                      <div className="absolute inset-0 bg-gray-100/80 backdrop-blur-sm rounded-lg border border-gray-200/60"></div>
                    )}
                    <div className="relative flex items-center gap-2">
                      <Layers className="w-4 h-4" />
                      Multi Race
                    </div>
                  </Link>
                  <Link
                    to="/dashboard"
                    className={`relative text-gray-600 hover:text-gray-800 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive('/dashboard') ? 'text-gray-800' : 'hover:bg-white/30'
                    }`}
                  >
                    {isActive('/dashboard') && (
                      <div className="absolute inset-0 bg-gray-100/80 backdrop-blur-sm rounded-lg border border-gray-200/60"></div>
                    )}
                    <div className="relative flex items-center gap-2">
                      <BarChart2 className="w-4 h-4" />
                      Dashboard
                    </div>
                  </Link>
                  
                  {/* Profile Dropdown */}
                  <div ref={dropdownRef} className="relative ml-4 pl-4 border-l border-white/30">
                    <button
                      onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                      className="flex items-center gap-2 text-gray-600 hover:text-gray-800 px-3 py-1.5 text-sm font-medium disabled:opacity-50 transition-all duration-200"
                    >
                      {userProfile?.profile_photo_url ? (
                        <img
                          src={userProfile.profile_photo_url}
                          alt="Profile"
                          className="w-7 h-7 rounded-full object-cover border border-gray-200"
                        />
                      ) : (
                        <div className="w-7 h-7 bg-gradient-to-br from-gray-700 to-gray-900 rounded-full flex items-center justify-center text-xs font-medium text-white shadow-sm">
                          {getInitials(userProfile?.first_name, userProfile?.last_name, user?.email)}
                        </div>
                      )}
                      <span className="max-w-[100px] truncate">
                        {getDisplayName()}
                      </span>
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isProfileDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    <div 
                      className={`absolute right-0 mt-2 w-56 z-50 transform origin-top-right transition-all duration-200 ease-out ${
                        isProfileDropdownOpen 
                          ? 'opacity-100 scale-100' 
                          : 'opacity-0 scale-95 pointer-events-none'
                      }`}
                    >
                      <LiquidGlass 
                        className="py-2 shadow-xl" 
                        bgOpacity="ultra"
                        variant="card"
                      >
                        <div className="px-4 py-3 border-b border-gray-100/50">
                          <p className="text-sm font-medium text-gray-800 truncate">
                            {getFullDisplayName()}
                          </p>
                          <p className="text-xs text-gray-500 truncate mt-1">
                            {user?.email}
                          </p>
                        </div>
                        
                        <div className="py-1">
                          <Link
                            to="/profile"
                            onClick={closeMenu}
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-white/40 transition-colors duration-200 group"
                          >
                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                              <User className="w-4 h-4 text-gray-600" />
                            </div>
                            <div>
                              <span className="font-medium">Profile</span>
                              <p className="text-xs text-gray-500">Manage your account</p>
                            </div>
                          </Link>
                          
                          <Link
                            to="/organization"
                            onClick={closeMenu}
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-white/40 transition-colors duration-200 group"
                          >
                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                              <Building className="w-4 h-4 text-gray-600" />
                            </div>
                            <div>
                              <span className="font-medium">Organization</span>
                              <p className="text-xs text-gray-500">Team settings</p>
                            </div>
                          </Link>
                        </div>
                        
                        <div className="border-t border-gray-100/50 py-1">
                          <button
                            onClick={handleSignOut}
                            disabled={signingOut}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-white/40 disabled:opacity-50 transition-colors duration-200 group"
                          >
                            <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center group-hover:bg-red-100 transition-colors">
                              <LogOut className="w-4 h-4 text-red-600" />
                            </div>
                            <span className="font-medium">{signingOut ? 'Signing out...' : 'Sign Out'}</span>
                          </button>
                        </div>
                      </LiquidGlass>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>

        {user && (
          <div
            className={`${
              isMenuOpen ? 'block' : 'hidden'
            } sm:hidden border-t border-white/30`}
          >
            <div className="pt-2 pb-3 space-y-1 container mx-auto px-4">
              {/* Main Navigation Section */}
              {isSuperAdmin && (
                <Link
                  to="/super-admin"
                  onClick={closeMenu}
                  className={`relative block px-4 py-2 text-base font-medium rounded-lg transition-all duration-200 ${
                    isActive('/super-admin')
                      ? 'text-gray-800'
                      : 'text-gray-600 hover:bg-white/30 hover:text-gray-800'
                  }`}
                >
                  {isActive('/super-admin') && (
                    <div className="absolute inset-0 bg-gray-100/80 backdrop-blur-sm rounded-lg border border-gray-200/60"></div>
                  )}
                  <div className="relative flex items-center gap-2">
                    <Crown className="w-4 h-4" />
                    Super Admin
                  </div>
                </Link>
              )}
              <Link
                to="/results"
                onClick={closeMenu}
                className={`relative block px-4 py-2 text-base font-medium rounded-lg transition-all duration-200 ${
                  isActive('/results')
                    ? 'text-gray-800'
                    : 'text-gray-600 hover:bg-white/30 hover:text-gray-800'
                }`}
              >
                {isActive('/results') && (
                  <div className="absolute inset-0 bg-gray-100/80 backdrop-blur-sm rounded-lg border border-gray-200/60"></div>
                )}
                <div className="relative flex items-center gap-2">
                  <Trophy className="w-4 h-4" />
                  Results
                </div>
              </Link>
              <Link
                to="/events"
                onClick={closeMenu}
                className={`relative block px-4 py-2 text-base font-medium rounded-lg transition-all duration-200 ${
                  isActive('/events')
                    ? 'text-gray-800'
                    : 'text-gray-600 hover:bg-white/30 hover:text-gray-800'
                }`}
              >
                {isActive('/events') && (
                  <div className="absolute inset-0 bg-gray-100/80 backdrop-blur-sm rounded-lg border border-gray-200/60"></div>
                )}
                <div className="relative flex items-center gap-2">
                  <CalendarDays className="w-4 h-4" />
                  Events
                </div>
              </Link>
              <Link
                to="/multi-race"
                onClick={closeMenu}
                className={`relative block px-4 py-2 text-base font-medium rounded-lg transition-all duration-200 ${
                  isActive('/multi-race')
                    ? 'text-gray-800'
                    : 'text-gray-600 hover:bg-white/30 hover:text-gray-800'
                }`}
              >
                {isActive('/multi-race') && (
                  <div className="absolute inset-0 bg-gray-100/80 backdrop-blur-sm rounded-lg border border-gray-200/60"></div>
                )}
                <div className="relative flex items-center gap-2">
                  <Layers className="w-4 h-4" />
                  Multi Race
                </div>
              </Link>
              <Link
                to="/dashboard"
                onClick={closeMenu}
                className={`relative block px-4 py-2 text-base font-medium rounded-lg transition-all duration-200 ${
                  isActive('/dashboard')
                    ? 'text-gray-800'
                    : 'text-gray-600 hover:bg-white/30 hover:text-gray-800'
                }`}
              >
                {isActive('/dashboard') && (
                  <div className="absolute inset-0 bg-gray-100/80 backdrop-blur-sm rounded-lg border border-gray-200/60"></div>
                )}
                <div className="relative flex items-center gap-2">
                  <BarChart2 className="w-4 h-4" />
                  Dashboard
                </div>
              </Link>
              
              {/* Enhanced Visual Separator */}
              <div className="py-3 mx-2">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/50"></div>
                  </div>
                  <div className="relative flex justify-center">
                    <span className="px-3 bg-white/20 backdrop-blur-sm text-xs text-gray-500 font-medium rounded-full">
                      Account
                    </span>
                  </div>
                </div>
              </div>
              
              {/* User Account Section */}
              <Link
                to="/profile"
                onClick={closeMenu}
                className="flex items-center gap-2 px-4 py-2 text-base font-medium text-gray-600 hover:bg-white/30 hover:text-gray-800 rounded-lg transition-all duration-200"
              >
                <User className="w-4 h-4" />
                Profile
              </Link>
              <Link
                to="/organization"
                onClick={closeMenu}
                className="flex items-center gap-2 px-4 py-2 text-base font-medium text-gray-600 hover:bg-white/30 hover:text-gray-800 rounded-lg transition-all duration-200"
              >
                <Building className="w-4 h-4" />
                Organization
              </Link>
              <button
                onClick={handleSignOut}
                disabled={signingOut}
                className="w-full flex items-center gap-2 px-4 py-2 text-base font-medium text-gray-600 hover:bg-white/30 hover:text-gray-800 disabled:opacity-50 rounded-lg transition-all duration-200"
              >
                <LogOut className="w-4 h-4" />
                {signingOut ? 'Signing out...' : 'Sign Out'}
              </button>
            </div>
          </div>
        )}
      </LiquidGlass>
    </nav>
  );
}